import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import LinkButton from 'components/layout/buttons/LinkButton';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TrashIcon from 'components/layout/icons/TrashIcon';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import translations from 'services/translations/translations';
import { fetchGroups } from 'store/products/actions';

import getProductGroupOptions from '../offerProduct/getProductGroupOptions';

import 'style/offers/OpportunityEstimates.css';

export interface IEstimateRow {
  amount: string;
  group: string;
  key: number;
}

// The rows of the saved estimates of an opportunity
export const getEstimateRows = (
  estimates: { amount: number; group: string }[] | undefined | null
): IEstimateRow[] =>
  (estimates ?? []).map((estimate, index) => ({
    amount: formatGroupedNumber(estimate.amount, 2, 0).replace(/\s/g, ''),
    group: estimate.group,
    key: index + 1,
  }));

// The rows with a work type, as saved with the opportunity
export const getEstimatesPost = (
  rows: IEstimateRow[]
): { amount: number; group: string }[] =>
  rows
    .filter((row) => row.group !== '')
    .map((row) => ({
      amount: Math.max(0, numericStringToFloat(row.amount) || 0),
      group: row.group,
    }));

/**
 * The estimated sums of an opportunity by work type (e.g. suspended ceilings installed):
 * rows added with "+", and the total of the opportunity below them. An opportunity has no
 * calculation.
 */
const OpportunityEstimates = ({
  locked,
  rows,
  save,
  setRows,
}: {
  locked: boolean;
  rows: IEstimateRow[];
  // Saves the opportunity with its rows
  save?: () => void;
  setRows: (rows: IEstimateRow[]) => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.products]);
  const groups = useSelector((state: any) => state.products.groups);

  useEffect(() => {
    dispatch(fetchGroups());
  }, [dispatch]);

  const tr = (key: string) => t(`${translations.offers}:opportunity.${key}`);

  const updateRow = (key: number, changes: Partial<IEstimateRow>) =>
    setRows(
      rows.map((row) => (row.key === key ? { ...row, ...changes } : row))
    );

  const addRow = () =>
    setRows([
      ...rows,
      {
        amount: '',
        group: '',
        key: Math.max(0, ...rows.map((row) => row.key)) + 1,
      },
    ]);

  const total = rows.reduce(
    (sum, row) => sum + (numericStringToFloat(row.amount) || 0),
    0
  );

  return (
    <div className="opportunity-estimates">
      <p className="header-text">{tr('Estimates').toUpperCase()}</p>
      <p className="opportunity-estimates-info">{tr('Estimates info')}</p>
      {rows.length === 0 ? null : (
        <Row className="opportunity-estimates-header">
          <Col md={6}>{tr('Work type')}</Col>
          <Col md={4}>{tr('Estimated sum')}</Col>
        </Row>
      )}
      {rows.map((row) => (
        <Row key={row.key} className="opportunity-estimate-row">
          <Col md={6}>
            <SelectInputFieldControlled
              disabled={locked}
              id={`opportunity-estimate-group-${row.key}`}
              name={`opportunity-estimate-group-${row.key}`}
              onChange={(event: any) =>
                updateRow(row.key, { group: event.target.value })
              }
              options={getProductGroupOptions(groups, t)}
              value={row.group}
            />
          </Col>
          <Col md={4}>
            <NumberWithUnitInputField
              disabled={locked}
              id={`opportunity-estimate-amount-${row.key}`}
              name={`opportunity-estimate-amount-${row.key}`}
              onChange={(event: any) =>
                updateRow(row.key, { amount: event.target.value })
              }
              unit="€"
              value={row.amount}
            />
          </Col>
          <Col md={1}>
            {locked ? null : (
              <TrashIcon
                style={{ marginTop: '0.5rem' }}
                onClick={() =>
                  setRows(rows.filter((other) => other.key !== row.key))
                }
              />
            )}
          </Col>
        </Row>
      ))}
      {locked ? null : (
        <LinkButton onClick={addRow}>{`+ ${tr('Add estimate')}`}</LinkButton>
      )}
      <Row className="opportunity-estimates-total">
        <Col md={6}>{tr('Total')}</Col>
        <Col md={4} className="opportunity-estimates-total-sum">
          {`${formatGroupedNumber(total)} €`}
        </Col>
      </Row>
      {locked || save === undefined ? null : (
        <div className="opportunity-estimates-actions">
          <PrimaryButton className="button-one-line" onClick={save}>
            {tr('Save estimates').toLocaleUpperCase()}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

export default OpportunityEstimates;
